/* CompleteTheLook independent selection + approved product comparison behaviour. */
(() => {
  const fillPercent = (pattern, percent) => (pattern || '').replace(/\[\[percent\]\]/g, String(percent));
  const syncComparisons = (root, priceMinor, priceText) => {
    if (!root) return;
    root.querySelectorAll('[data-extras-selected-price]').forEach((cell) => { cell.textContent = priceText; });
    root.querySelectorAll('[data-retail-comparison]').forEach((comparison) => {
      const reference = Number(comparison.dataset.referencePrice);
      const shown = Number.isFinite(priceMinor) && reference > priceMinor && reference > 0;
      const saving = shown ? Math.round((reference - priceMinor) / reference * 100) : 0;
      comparison.querySelectorAll('[data-retail-reference]').forEach((part) => { part.hidden = !shown; });
      const text = comparison.querySelector('[data-retail-saving]');
      text.hidden = !shown || saving <= 0;
      text.textContent = fillPercent(comparison.dataset.savingPattern, saving);
      if (!shown) {
        comparison.querySelector('[data-retail-info]').hidden = true;
        comparison.querySelector('[data-retail-toggle]').setAttribute('aria-expanded', 'false');
      }
    });
  };
  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-product-view]').forEach((root) => {
      const initialComparison = root.querySelector('[data-retail-comparison]');
      const priceMinor = Number(root.dataset.currentPriceMinor ?? initialComparison?.dataset.initialPrice);
      const priceText = root.dataset.currentPriceText || root.querySelector('[data-current-price]')?.textContent || '';
      syncComparisons(root, priceMinor, priceText);
    });
    scope.querySelectorAll('[data-complete-look]').forEach((look) => {
      if (look.dataset.extrasInitialized) return;
      look.dataset.extrasInitialized = 'true';
      const colorIndex = Number(look.dataset.colorIndex);
      const sizeIndex = Number(look.dataset.sizeIndex);
      const nativeVariants = JSON.parse(look.querySelector('[data-look-variants]').textContent);
      const variants = sizeIndex >= 0 && window.WingsCommerce?.sortBySize ? window.WingsCommerce.sortBySize(nativeVariants, (variant) => variant.options[sizeIndex]) : nativeVariants;
      if (!variants.length) return;
      const form = look.querySelector('[data-look-form]');
      const nativeSelect = form.querySelector('select[name="id"]');
      nativeSelect.required = false;
      const colourSelect = look.querySelector('[data-look-color]');
      const first = variants.find((variant) => String(variant.id) === look.dataset.initialVariant) || variants.find((variant) => variant.available) || variants[0];
      let color = colorIndex >= 0 ? first.options[colorIndex] : null;
      let selectedId = null;
      let selected = null;
      const labels = Array.from(look.querySelectorAll('[data-look-size-label]'));
      const sizeGroup = look.querySelector('[data-look-sizes]');
      variants.forEach((variant) => {
        const label = labels.find((item) => item.dataset.lookSizeLabel === String(variant.id));
        if (label) sizeGroup.append(label);
      });
      const radios = Array.from(look.querySelectorAll('[data-look-size]'));
      const error = look.querySelector('[data-look-error]');
      const add = look.querySelector('[data-look-add]');
      const update = () => {
        const scoped = variants.filter((variant) => colorIndex < 0 || variant.options[colorIndex] === color);
        selected = scoped.find((variant) => String(variant.id) === selectedId) || (scoped.length === 1 ? scoped[0] : null);
        const representative = selected || scoped.reduce((minimum, variant) => !minimum || variant.price < minimum.price ? variant : minimum, null);
        if (!representative) return;
        const ids = new Set(scoped.map((variant) => String(variant.id)));
        labels.forEach((label) => { label.hidden = !ids.has(label.dataset.lookSizeLabel); });
        radios.forEach((radio) => {
          const variant = variants.find((item) => String(item.id) === radio.value);
          radio.disabled = !ids.has(radio.value) || !variant?.available;
          radio.checked = Boolean(selected && String(selected.id) === radio.value);
        });
        sizeGroup.hidden = scoped.length === 1 && sizeIndex < 0;
        nativeSelect.value = selected ? String(selected.id) : '';
        const onSale = representative.comparePrice > representative.price;
        const hasRange = !selected && scoped.some((variant) => variant.price > representative.price);
        look.querySelector('[data-look-current-price]').textContent = hasRange ? representative.fromText : representative.priceText;
        const compare = look.querySelector('[data-look-compare-price]');
        compare.hidden = !onSale;
        look.querySelector('[data-look-compare-text]').textContent = representative.compareText;
        look.querySelector('[data-look-sale-label]').hidden = !onSale;
        look.querySelector('[data-look-price]').classList.toggle('complete-look__price--sale', onSale);
        look.querySelectorAll('[data-look-image-color]').forEach((image) => { image.hidden = image.dataset.lookImageColor !== color; });
        if (colourSelect) {
          colourSelect.value = color;
          colourSelect.lang = /\p{Script=Arabic}/u.test(color || '') ? 'ar' : 'en';
        }
        const target = selected || scoped.find((variant) => variant.available) || representative;
        const url = new URL(look.dataset.baseUrl, window.location.origin);
        url.searchParams.set('variant', String(target.id));
        look.querySelectorAll('[data-look-link]').forEach((link) => { link.href = `${url.pathname}${url.search}`; });
        add.disabled = scoped.every((variant) => !variant.available);
      };
      const clearError = () => { error.hidden = true; look.querySelector('[data-look-error-text]').textContent = ''; sizeGroup.removeAttribute('aria-describedby'); };
      colourSelect?.addEventListener('change', () => {
        color = colourSelect.value;
        selectedId = null;
        clearError();
        update();
      });
      radios.forEach((radio) => radio.addEventListener('change', () => {
        if (radio.disabled || !radio.checked) return;
        selectedId = radio.value;
        clearError();
        update();
      }));
      form.addEventListener('submit', (event) => {
        if (selected?.available) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (!selected) {
          look.querySelector('[data-look-error-text]').textContent = look.dataset.selectLabel;
          error.hidden = false;
          sizeGroup.setAttribute('aria-describedby', error.id);
          radios.find((radio) => !radio.disabled)?.focus();
        }
      }, true);
      update();
    });
  };
  document.addEventListener('wings:product-price', (event) => {
    if (!event.detail) return;
    syncComparisons(event.target.closest('[data-product-view]'), Number(event.detail.priceMinor), event.detail.priceText);
  });
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-retail-toggle]');
    if (!button) return;
    const info = button.closest('[data-retail-comparison]').querySelector('[data-retail-info]');
    info.hidden = !info.hidden;
    button.setAttribute('aria-expanded', String(!info.hidden));
  });
  const dismiss = (event) => {
    if (event.type === 'keydown' && event.key !== 'Escape') return;
    document.querySelectorAll('[data-retail-comparison]').forEach((comparison) => {
      const info = comparison.querySelector('[data-retail-info]');
      if (info.hidden || (event.type === 'pointerdown' && comparison.contains(event.target))) return;
      info.hidden = true;
      const button = comparison.querySelector('[data-retail-toggle]');
      button.setAttribute('aria-expanded', 'false');
      if (event.type === 'keydown') button.focus({ preventScroll: true });
    });
  };
  document.addEventListener('pointerdown', dismiss);
  document.addEventListener('keydown', dismiss);
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
  initialise();
})();
